import React, { useEffect, useState } from "react";
import { styled } from "../../stitches.config";
import { withThemeContext } from "./StitchesThemeProvider";

import Youtube from "../../components/Article/Parts/Youtube";
import OnnetworkPlayer from "../../components/OnnetworkPlayer";
import TiktokEmbed from "../../components/Article/Content/body/TiktokEmbed";
import CloseIcon from "../../components/composition-components/gfx/v2/close_icon.png";

interface EmbedProps {
  article?: any;
}

const EmbedCarousel: React.FC<EmbedProps> = ({ article }) => {
  const embed = article?.embed;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <>
      {embed?.type === "tiktok" && (
        <StyledEmbedContainer>
          <EmbedWrapper type="tiktok">
            <EmbedPlaceholder
              embedText={embed.text}
              thumbnail={embed.thumbnail}
              onClick={() => setOpen(true)}
            />
            {embed.text && <Description>{embed.text}</Description>}
          </EmbedWrapper>
        </StyledEmbedContainer>
      )}

      {/* 
      {embed?.type === "onnetwork" && (
        <StyledEmbed>
          <EmbedWrapper type="onnetwork">
            <EmbedElement>
              <OnnetworkPlayer mid={embed.mid} />
            </EmbedElement>
          </EmbedWrapper>
        </StyledEmbed>
      )}

      {embed?.type === "youtube" && (
        <StyledEmbed>
          <EmbedWrapper type="youtube">
            <EmbedElement>
              <Youtube isOneSuper={false} videoId={embed.id} />
            </EmbedElement>
          </EmbedWrapper>
        </StyledEmbed>
      )}
      */}

      {open && embed?.type === "tiktok" && (
        <ModalOverlay onClick={() => setOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setOpen(false)}>
              <img src={CloseIcon} alt="close" />
            </CloseButton>

            <TiktokEmbed
              profile={embed.profile}
              videoId={embed.videoId}
              noWrapper={true}
            />
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

const StyledEmbed = styled("div", {
  display: "flex",
  justifyContent: "center",
  margin: "0 auto",
});

const Description = styled("p", {
  maxHeight: "62px",
  overflow: "hidden",
  color: "$grayscale0Fixed",
  position: "absolute",
  bottom: 0,
  zIndex: 2,
  width: "160px",
  padding: "0px 12px",
  fontSize: "14px",

  "@bp5": {
    width: "190px",
  },
});

const StyledEmbedContainer = styled("div", {
  display: "block",
  maxWidth: "160px",

  "@bp5": {
    maxWidth: "190px",
  },

});

const CloseButton = styled("button", {
  position: "absolute",
  top: "16px",
  right: "16px",
  width: "36px",
  height: "36px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,

  "@bp4": {
    top: "60px",
    right: "80px",
  },

  img: {
    width: "24px",
    "@bp4": {
      width: "40px"
    },
  }
});

const EmbedWrapper = styled("div", {
  variants: {
    type: {
      tiktok: {
        width: "160px",
        height: "300px",
        borderRadius: "12px",
        overflow: "hidden",
        cursor: "pointer",
        backgroundColor: "#000",
        position: "relative",

        "@bp5": {
          width: "190px",
        },
      },
      youtube: {
        height: "200px",
        width: "300px",
      },
      onnetwork: {
        height: "200px",
        width: "300px",
      },
    },
  },
});

const EmbedElement = styled("span", {
  display: "block",
});

const PlaceholderWrapper = styled("div", {
  position: "relative",
  width: "100%",
  height: "100%",
  backgroundSize: "cover",
  backgroundPosition: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.2s ease",
  overflow: "hidden",

  "&:hover": {
    transform: "scale(1.03)",
  },

  variants: {
    withGradient: {
      true: {
        "&::after": {
          content: "",
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `linear-gradient(
            to bottom,
            hsla(0, 0%, 0%, 0) 0%,
            hsla(0, 0%, 0%, 0.013) 8.1%,
            hsla(0, 0%, 0%, 0.049) 15.5%,
            hsla(0, 0%, 0%, 0.104) 22.5%,
            hsla(0, 0%, 0%, 0.175) 29%,
            hsla(0, 0%, 0%, 0.259) 35.3%,
            hsla(0, 0%, 0%, 0.352) 41.2%,
            hsla(0, 0%, 0%, 0.45) 47.1%,
            hsla(0, 0%, 0%, 0.55) 52.9%,
            hsla(0, 0%, 0%, 0.648) 58.8%,
            hsla(0, 0%, 0%, 0.741) 64.7%,
            hsla(0, 0%, 0%, 0.825) 71%,
            hsla(0, 0%, 0%, 0.896) 77.5%,
            hsla(0, 0%, 0%, 0.951) 84.5%,
            hsla(0, 0%, 0%, 0.987) 91.9%,
            hsl(0, 0%, 0%) 100%
          )`,
          zIndex: 1,
        },
      },
    },
  },
});

const PlayButton = styled("div", {
  width: "44px",
  height: "44px",
  position: "relative",
  zIndex: 2,
  borderRadius: "50%",
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  fontSize: "18px",
});

const EmbedPlaceholder = ({
  embedText,
  thumbnail,
  onClick,
}: {
  embedText: string;
  thumbnail: string;
  onClick: () => void;
}) => (
  <PlaceholderWrapper
    withGradient={!!embedText}
    css={{ backgroundImage: `url(${thumbnail})` }}
    onClick={onClick}
  >
    <PlayButton>▶</PlayButton>
  </PlaceholderWrapper>
);

const ModalOverlay = styled("div", {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.9)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 999999,
});

const ModalContent = styled("div", {
  width: "100%",
  maxWidth: "324px",
  height: "90vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  ".tiktok-embed-wrapper": {
    //width: "217px",
    width: "100%",

    "@bp4": {
      width: "100%",
    },

    ".embed-iframe-wrapper": {
      borderRadius: "10px",
      maxWidth: "360px",
      //height: "506px !important",

      "@bp4": {
        height: "756px !important",
      },
    },
  },
});

export default withThemeContext(EmbedCarousel);